<template>
  <container>
    <h1 style="font-size:25px;text-align:center;position:relative;">
      MongoDB Explorer
    </h1>

    <!-- Section 1: Collections -->
    <div class="stats-section">
      <div class="section-header">
        <h2>Collections</h2>
        <button
          class="btn btn--secondary"
          style="margin-left:auto;"
          @click="loadCollections"
          :disabled="collectionsLoading"
          title="Refresh collections"
        >
          Refresh
        </button>
      </div>

      <div v-if="collectionsLoading" class="msg-center">
        <p>Loading collections...</p>
      </div>

      <div v-if="collectionsError" class="msg-center msg-error">
        <p style="margin-bottom:1rem;">{{ collectionsError }}</p>
        <button class="btn btn--primary" @click="loadCollections">Retry</button>
      </div>

      <div v-if="!collectionsLoading && !collectionsError && collections.length" class="collections-grid">
        <button
          v-for="col in collections"
          :key="col"
          class="collection-chip"
          :class="{ 'collection-chip--active': selectedCollection === col }"
          @click="selectCollection(col)"
        >
          {{ col }}
        </button>
      </div>
    </div>

    <!-- Section 2: Document Lookup -->
    <div v-if="selectedCollection" class="stats-section">
      <div class="section-header">
        <h2>Lookup in <span class="collection-name">{{ selectedCollection }}</span></h2>
      </div>

      <div class="lookup-row">
        <input
          id="mongo-doc-id-input"
          class="doc-id-input"
          type="text"
          v-model="documentId"
          placeholder="Enter document ID (e.g. race ID or run ID)"
          @keyup.enter="fetchDocument"
        />
        <button
          id="mongo-fetch-btn"
          class="btn btn--primary"
          @click="fetchDocument"
          :disabled="!documentId || documentId.trim() === '' || fetchLoading"
        >
          {{ fetchLoading ? 'Fetching...' : 'Fetch' }}
        </button>
      </div>

      <div v-if="fetchError" class="msg-center msg-error" style="margin-top:1rem;">
        <p>{{ fetchError }}</p>
      </div>
    </div>

    <!-- Section 3: Result -->
    <div v-if="fetchAttempted" class="stats-section">
      <div class="section-header">
        <h2>Result</h2>
        <span v-if="document" class="last-updated">
          {{ Object.keys(document).length }} top-level keys
        </span>
      </div>

      <div v-if="fetchLoading" class="msg-center">
        <p>Loading document...</p>
      </div>

      <div v-if="!fetchLoading && !document && !fetchError" class="msg-center" style="color:#666;">
        <p>No document found for ID <strong>{{ lastQueriedId }}</strong> in <strong>{{ selectedCollection }}</strong>.</p>
      </div>

      <div v-if="!fetchLoading && document">
        <!-- Top-level keys summary -->
        <div class="stats-grid" style="margin-bottom:1.5rem;">
          <div
            v-for="key in topLevelKeys"
            :key="key"
            class="stat-card"
          >
            <div class="stat-label">{{ key }}</div>
            <div class="stat-value-small">{{ formatValue(document[key]) }}</div>
          </div>
        </div>

        <!-- Toggle full JSON -->
        <div style="text-align:center; margin-top:1rem;">
          <button class="btn btn--secondary" @click="showFullJson = !showFullJson">
            {{ showFullJson ? 'Hide Full JSON' : 'Show Full JSON' }}
          </button>
        </div>

        <div v-if="showFullJson" class="json-viewer">
          <pre>{{ prettyJson }}</pre>
        </div>
      </div>
    </div>
  </container>
</template>

<script>
import Container from '@/components/Container'
import MongoExplorerService from '@/services/mongoExplorer'

export default {
  name: 'mongo-explorer',
  data: function() {
    return {
      // Collections
      collections: [],
      collectionsLoading: false,
      collectionsError: null,
      selectedCollection: null,

      // Document lookup
      documentId: '',
      fetchLoading: false,
      fetchError: null,
      fetchAttempted: false,
      lastQueriedId: '',

      // Result
      document: null,
      showFullJson: false
    }
  },
  computed: {
    topLevelKeys: function() {
      if (!this.document) return []
      return Object.keys(this.document).slice(0, 12) // Cap display to 12 top-level keys
    },
    prettyJson: function() {
      if (!this.document) return ''
      try {
        return JSON.stringify(this.document, null, 2)
      } catch (e) {
        return String(this.document)
      }
    }
  },
  methods: {
    loadCollections: function() {
      var self = this
      self.collectionsLoading = true
      self.collectionsError = null

      MongoExplorerService.getCollections().then(function(response) {
        var data = response && response.data
        if (data && data.success && data.data && data.data.collections) {
          self.collections = data.data.collections
        } else {
          self.collectionsError = 'Failed to load collections'
        }
      }).catch(function(err) {
        var msg = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || 'Failed to load collections'
        self.collectionsError = msg
        console.error('[MongoExplorer] Error loading collections:', err)
      }).then(function() {
        // finally
        self.collectionsLoading = false
      })
    },

    selectCollection: function(col) {
      this.selectedCollection = col
      this.documentId = ''
      this.document = null
      this.fetchError = null
      this.fetchAttempted = false
      this.showFullJson = false
    },

    fetchDocument: function() {
      var trimmedId = this.documentId && this.documentId.trim()
      if (!trimmedId || !this.selectedCollection) return

      var self = this
      self.fetchLoading = true
      self.fetchError = null
      self.fetchAttempted = true
      self.document = null
      self.lastQueriedId = trimmedId
      self.showFullJson = false

      MongoExplorerService.getDocument(self.selectedCollection, trimmedId).then(function(response) {
        var data = response && response.data
        if (data && data.success && data.data && data.data.document) {
          self.document = data.data.document
        } else {
          self.document = null
        }
      }).catch(function(err) {
        var status = err && err.response && err.response.status
        if (status === 404) {
          // Not found — show the "no document" state, not an error
          self.document = null
          self.fetchError = null
        } else {
          var msg = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || 'Failed to fetch document'
          self.fetchError = msg
        }
        console.error('[MongoExplorer] Error fetching document:', err)
      }).then(function() {
        // finally
        self.fetchLoading = false
      })
    },

    formatValue: function(val) {
      if (val === null || val === undefined) return '—'
      if (typeof val === 'object') {
        var keys = Object.keys(val)
        return '{' + keys.length + ' keys}'
      }
      if (Array.isArray(val)) return '[' + val.length + ' items]'
      var str = String(val)
      return str.length > 60 ? str.substring(0, 60) + '…' : str
    }
  },
  created: function() {
    this.loadCollections()
  },
  components: {
    Container
  }
}
</script>

<style scoped>
.stats-section {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 1.5rem;
  margin: 20px 0;
  background: #fff;
  box-shadow: 0 0 10px rgba(0, 0, 0, .05);
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #eee;
}

.section-header h2 {
  font-size: 20px;
  margin: 0;
  color: #423799;
}

.collection-name {
  font-weight: 700;
  color: #423799;
}

.last-updated {
  margin-left: auto;
  font-size: 12px;
  color: #666;
}

.msg-center {
  text-align: center;
  padding: 2rem;
}

.msg-error {
  color: #c0392b;
}

/* Collection chips */
.collections-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.collection-chip {
  padding: 8px 18px;
  border: 2px solid #ddd;
  border-radius: 20px;
  background: #f9f9f9;
  color: #555;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}

.collection-chip:hover {
  border-color: #423799;
  color: #423799;
  background: #f0eeff;
}

.collection-chip--active {
  border-color: #423799;
  background: #423799;
  color: #fff;
}

.collection-chip--active:hover {
  background: #352c7a;
  border-color: #352c7a;
  color: #fff;
}

/* Lookup row */
.lookup-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.doc-id-input {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.doc-id-input:focus {
  border-color: #423799;
}

/* Stats grid (reused from SystemSettings) */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.stat-card {
  padding: 1rem;
  background: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #eee;
}

.stat-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  word-break: break-word;
}

.stat-value-small {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  word-break: break-all;
}

/* JSON viewer */
.json-viewer {
  margin-top: 1.5rem;
  background: #1e1e2e;
  border-radius: 6px;
  padding: 1.5rem;
  overflow-x: auto;
}

.json-viewer pre {
  margin: 0;
  font-family: 'Fira Mono', 'Consolas', 'Monaco', monospace;
  font-size: 12px;
  line-height: 1.6;
  color: #cdd6f4;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Buttons */
.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: background-color 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  white-space: nowrap;
}

.btn--primary {
  background: #423799;
  color: white;
}

.btn--primary:hover:not(:disabled) {
  background: #352c7a;
}

.btn--primary:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.btn--secondary {
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
}

.btn--secondary:hover:not(:disabled) {
  background: #e0e0e0;
}

.btn--secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// MongoExplorer.vue?47b71adb